import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {GatewayError} from '@yandex-data-ui/gateway';

import {
    WikiSearchAuthor,
    WikiSearchData,
    WikiSearchDocumentType,
    WikiSearchOrderType,
} from '~shared/models/wiki';

import {initialState} from './state';
import {parseSearchQuery} from './helpers';
import {SearchDateFilterType, SearchDateRange} from './types';

export const {reducer, actions} = createSlice({
    name: 'search',
    initialState,
    reducers: {
        init: {
            prepare(search: string, isMobile: boolean) {
                return {payload: search, meta: {isMobile}};
            },
            reducer(state, {payload, meta}: PayloadAction<string, string, {isMobile: boolean}>) {
                state.status = 'initial';
                Object.assign(state.params, parseSearchQuery(payload, meta.isMobile));
            },
        },
        destruct: () => initialState,

        submitSearch() {},

        changeSearchText(state, {payload}: PayloadAction<string>) {
            state.params.text = payload;
        },

        setSearchLoading(state) {
            state.status = 'search-loading';
        },

        changePage(state, {payload}: PayloadAction<number>) {
            state.status = 'page-loading';
            state.params.page = payload;
        },
        resetPage(state) {
            state.params.page = 1;
        },

        changeOrder(state, {payload}: PayloadAction<WikiSearchOrderType>) {
            state.params.order = payload;
        },

        fetchResults() {},
        fetchResultsSuccess(state, {payload}: PayloadAction<WikiSearchData>) {
            state.data = payload;
            state.status = 'loaded';
        },
        fetchResultsFailed(state, {payload}: PayloadAction<GatewayError>) {
            state.error = payload;
            state.status = state.status === 'page-loading' ? 'page-error' : 'search-error';
        },

        fetchNextChunk(state) {
            state.params.page += 1;
            state.status = 'page-loading';
        },
        fetchNextChunkSuccess(state, {payload}: PayloadAction<WikiSearchData>) {
            const results = [...(state.data?.results ?? []), ...payload.results];

            state.status = 'loaded';
            state.data = payload;
            state.data.results = results;
        },

        changeAuthorFilter(state, {payload}: PayloadAction<WikiSearchAuthor[]>) {
            state.params.authors = payload;
        },
        changeDateFilter(state, {payload}: PayloadAction<SearchDateRange>) {
            state.params.date = payload;
        },
        changeDateTypeFilter(state, {payload}: PayloadAction<SearchDateFilterType>) {
            if (state.params.date) {
                state.params.date.type = payload;
            } else {
                state.params.date = {
                    type: payload,
                };
            }
        },
        changeDocumentTypeFilter(state, {payload}: PayloadAction<WikiSearchDocumentType>) {
            state.params.documentType = payload;
        },
        setShowObsolete(state, {payload}: PayloadAction<boolean>) {
            state.params.showObsolete = payload;
        },
        changeSlugFilter(state, {payload}: PayloadAction<string>) {
            state.params.slug = payload;
        },
        selectSlugFilter(state, {payload}: PayloadAction<string>) {
            state.params.slug = payload;
        },
        resetFilters(state) {
            state.params.date = undefined;
            state.params.slug = undefined;
            state.params.authors = undefined;
            state.params.showObsolete = false;
            state.params.documentType = WikiSearchDocumentType.ALL;
        },
    },
});
